import React from 'react';
import PropTypes from 'prop-types';

import { Editor as SlateEditor } from 'slate-react';
import ReactTooltip from 'react-tooltip';
import { css } from 'emotion';

import MarkMenu from './MarkMenu/MarkMenu';
import BlockMenu from './BlockMenu/BlockMenu';

import EditBlockquote from 'slate-edit-blockquote';
import PasteLinkify from 'slate-paste-linkify';
import EscapeHeader from './plugins/slate-escape-header';
import EscapeList from './plugins/slate-escape-list';
import EmbedTweet from './plugins/slate-embed-tweet';
import EmbedYouTube from './plugins/slate-embed-youtube';
import Hotkeys from './plugins/slate-hotkeys';

import Spinner from './styled/Spinner';
import Tweet from './styled/Tweet';
import YouTube from './styled/YouTube';

import IframeNode from './BlockMenu/dialogues/Code/nodes/Iframe';
import CodeNode from './BlockMenu/dialogues/Code/nodes/Code';

import Types from 'slate-prop-types';
import { urlProp } from './utils/propValidators';

class Editor extends React.Component {
  constructor(props) {
    super(props);
    this.updateMarkMenu = this.updateMarkMenu.bind(this);
    this.updateBlockMenu = this.updateBlockMenu.bind(this);

    const {authorization, oembedAPI} = props;

    this.plugins = [
      Hotkeys(),
      EscapeHeader(),
      EscapeList(),
      EditBlockquote(),
      EmbedTweet({
        oembedAPI: oembedAPI + 'twitter/',
        authorization,
      }),
      EmbedYouTube({
        oembedAPI: oembedAPI + 'youtube/',
        authorization,
      }),
      PasteLinkify({
        type: 'link',
        collapseTo: 'end',
      }),
    ];
  }

  componentDidMount() {
    this.updateMarkMenu();
    this.updateBlockMenu();
  }

  componentDidUpdate() {
    this.updateMarkMenu();
    this.updateBlockMenu();
    ReactTooltip.rebuild();
  }

  updateBlockMenu() {
    const { value } = this.props;
    const menu = this.blockMenu;
    if (!menu) return;

    if (value.isBlurred) {
      menu.style.opacity = 0;
      return;
    }

    if (!this.editorContainer || !window.getSelection()) return;
    const { left } = this.editorContainer.getBoundingClientRect();
    try {
      const rect = window.getSelection().getRangeAt(0).getBoundingClientRect();
      menu.style.top = `${rect.top + window.pageYOffset - 10}px`;
      menu.style.left = `${left - 44}px`;
      menu.style.opacity = 1;
      menu.style.pointerEvents = 'all';
    } catch (error) {}
  }

  updateMarkMenu() {
    const { value } = this.props;
    const menu = this.markMenu;
    if (!menu) return;

    if (value.isEmpty) {
      menu.style.pointerEvents = 'none';
      menu.style.opacity = 0;
    }
    if (value.isBlurred || value.isEmpty) return;

    let rect;
    try {
      rect = window.getSelection()
        .getRangeAt(0)
        .getBoundingClientRect();
    } catch (e) { return; }

    const leftOffset = rect.left + window.pageXOffset -
      menu.offsetWidth / 2 + rect.width / 2;
    const topOffset = rect.top + window.pageYOffset - menu.offsetHeight;

    menu.style.left = `${leftOffset}px`;
    menu.style.top = `${topOffset}px`;
    menu.style.opacity = 1;
    menu.style.pointerEvents = 'all';
  }

  renderMark(props) {
    const { children, mark, attributes } = props;

    switch (mark.type) {
      case 'bold':
        return <strong {...attributes}>{children}</strong>;
      case 'code':
        return <code {...attributes}>{children}</code>;
      case 'italic':
        return <em {...attributes}>{children}</em>;
      case 'underlined':
        return <u {...attributes}>{children}</u>;
      case 'small':
        return <small {...attributes}>{children}</small>;
    }
  }

  renderNode(props) {
    const { attributes, children, node } = props;

    switch (node.type) {
      case 'blockquote':
        return <blockquote {...attributes}>{children}</blockquote>;
      case 'bulleted-list':
        return <ul {...attributes}>{children}</ul>;
      case 'heading-three':
        return <h3 {...attributes}>{children}</h3>;
      case 'list-item':
        return <li {...attributes}>{children}</li>;
      case 'numbered-list':
        return <ol {...attributes}>{children}</ol>;
      case 'paragraph':
        return <p {...attributes}>{children}</p>;
      case 'link':
        return <a
          {...attributes}
          href={node.data.get('href')}
          data-tip={node.data.get('href')}
        >{children}</a>;
      case 'loading-spinner':
        return <Spinner {...attributes} className='fas fa-spinner fa-spin fa-2x' />;
      case 'tweet':
        return <div {...attributes} className='tweet-node'>
          <Tweet>
            <span dangerouslySetInnerHTML={{__html: node.data.get('html')}} />
          </Tweet>
        </div>;
      case 'youtube':
        return <div {...attributes} className='youtube-node'>
          <YouTube className='youtube-embed'>
            <span dangerouslySetInnerHTML={{__html: node.data.get('html')}} />
          </YouTube>
        </div>;
      case 'image':
        return <figure {...attributes}>
          <img src={node.data.get('canonical')} />
        </figure>;
      case 'code':
        return <CodeNode
          attributes={attributes}
          code={node.data.get('code')}
        />;
      case 'iframe':
        return <IframeNode
          attributes={attributes}
          code={node.data.get('code')}
        />;
    }
  }

  // Render the editor.
  render() {
    const { value, onChange, placeholder } = this.props;
    return (
      <div
        ref={ref => { this.editorContainer = ref; }}
      >
        <SlateEditor
          ref={ref => { this.editorComponent = ref; }}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          renderMark={this.renderMark}
          renderNode={this.renderNode}
          plugins={this.plugins}
        />
        <MarkMenu
          innerRef={ref => (this.markMenu = ref)}
          editor={this.editorComponent}
          {...this.props}
        />
        <BlockMenu
          innerRef={ref => (this.blockMenu = ref)}
          editor={this.editorComponent}
          {...this.props}
        />
        <ReactTooltip
          place='bottom'
          data-offset={{top: 5}}
          className={css`
            opacity: 1 !important;
            background-color: #fff !important;
            color: #888 !important;
            font-weight: bold;
            border: 1px solid #ccc;
            padding: 5px 10px !important;
            box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
            &::before, &::after{
              display: none;
            }
          `}
        />
      </div>
    );
  }
}

Editor.propTypes = {
  value: Types.value.isRequired,
  onChange: PropTypes.func.isRequired,
  oembedAPI: urlProp,
  imageAPI: urlProp,
  authorization: PropTypes.string.isRequired,
};

export default Editor;
